import { useEffect, useMemo, useState } from "react";
import { Button, message, Space } from "antd";
import PvTable from "../../components/Table/PvTable";
import ConfirmDelete from "../../components/ConfirmDelete/ConfirmDelete";
import Popup from "../../components/Popup";
import PvForm from "../../components/Form/PvForm";
import useSuperAdminService from "../../services/SuperAdmin/superAdminService";
import useAxios from "../../hooks/useAxios";

const ROLE_OPTIONS = [
  { name: "xadmin", label: "Super Admin (xadmin)" },
  { name: "positionsAdmin", label: "Positions Admin" },
  { name: "ebulAdmin", label: "E-Bulletin Admin" },
  { name: "ouberAdmin", label: "OUber Admin"}
];

const ROLE_LABELS = {
  xadmin: "Super Admin",
  positionsAdmin: "Positions Admin",
  ebulAdmin: "E-Bulletin Admin",
};

const AdminRoles = () => {
  const {
    getAllAdminRoles,
    listLoading,
    listResponse,
    addAdminRole,
    removeAdminRole,
  } = useSuperAdminService();

  const axiosEmployees = useAxios();

  const [tableData, setTableData] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    getAllAdminRoles();
    axiosEmployees.fetchData({ url: `/admin/employee`, method: "GET" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!listResponse?.data) return;

    const mapped = listResponse.data.map((item, index) => ({
      key: index + 1,
      id: item.id,
      employee_name: item.employee
        ? [item.employee.first_name, item.employee.middle_name, item.employee.last_name]
            .filter(Boolean)
            .join(" ")
        : `Employee ${item.employee_id}`,
      role_display: ROLE_LABELS[item.role] ?? item.role,
    }));
    setTableData(mapped);
  }, [listResponse]);

  useEffect(() => {
    if (!axiosEmployees.response?.data) return;

    setEmployeeOptions(
      axiosEmployees.response.data.map((emp) => ({
        name: emp.id,
        label: [emp.first_name, emp.middle_name, emp.last_name]
          .filter(Boolean)
          .join(" "),
      }))
    );
  }, [axiosEmployees.response]);

  const handleRemove = async (id) => {
    try {
      await removeAdminRole(id);
      message.success("Admin role removed.");
      getAllAdminRoles();
    } catch {
      message.error("Failed to remove admin role.");
    }
  };

  const columns = [
    { title: "No.", dataIndex: "key", rowScope: "row", width: 60 },
    { title: "Employee", dataIndex: "employee_name", key: "employee_name" },
    { title: "Role", dataIndex: "role_display", key: "role_display" },
    {
      title: "Action",
      key: "action",
      render: (_, item) => (
        <ConfirmDelete
          isService={true}
          buttonText="Remove"
          onDeleted={() => handleRemove(item.id)}
        />
      ),
    },
  ];

  const formFields = useMemo(
    () => [
      {
        name: "employee_id",
        label: "Employee",
        type: "select",
        selectItems: employeeOptions,
        rules: [{ required: true, message: "Please select an employee." }],
      },
      {
        name: "role",
        label: "Admin Role",
        type: "select",
        selectItems: ROLE_OPTIONS,
        rules: [{ required: true, message: "Please select a role." }],
      },
    ],
    [employeeOptions]
  );

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button
          size="large"
          type="primary"
          style={{ background: "#00573F" }}
          onClick={() => setIsModalOpen(true)}
        >
          Add Admin Role
        </Button>
      </Space>

      <PvTable
        data={tableData}
        columns={columns}
        loading={listLoading || axiosEmployees.loading}
        onRowClick={() => {}}
      />

      <Popup
        title="Add Admin Role"
        isOpen={isModalOpen}
        footer={null}
        onCancel={() => setIsModalOpen(false)}
      >
        <PvForm
          isService={true}
          formFields={formFields}
          onSubmitted={async (values) => {
            try {
              await addAdminRole(values.employee_id, values.role);
              message.success("Admin role added.");
              setIsModalOpen(false);
              getAllAdminRoles();
            } catch {
              message.error("Failed to add admin role.");
            }
          }}
        />
      </Popup>
    </>
  );
};

export default AdminRoles;
